import { datadogRum } from '@datadog/browser-rum'
import { addReactError, reactPlugin } from '@datadog/browser-rum-react'
import type { ErrorInfo } from 'react'

import { isDev } from '~/notes/flags'
import { getUserFullName, getUserReadOnly } from '~/notes/utils/cookies'

export const initializeDatadog = () => {
  const {
    CUSTOMER,
    DD_RUM_APP_ID,
    DD_RUM_CLIENT_TOKEN,
    DD_RUM_ENV,
    VERSION,
    location,
  } = window

  const canUseDatadog =
    typeof DD_RUM_APP_ID === 'string' &&
    typeof DD_RUM_CLIENT_TOKEN === 'string' &&
    !getUserReadOnly()

  if (!canUseDatadog) {
    // eslint-disable-next-line no-console
    console.warn('Datadog RUM not initialized')
    return
  }

  datadogRum.init({
    applicationId: DD_RUM_APP_ID,
    clientToken: DD_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'notes-frontend',
    env: DD_RUM_ENV,
    version: VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 5,
    sessionPersistence: 'local-storage',
    defaultPrivacyLevel: 'mask-user-input',
    // enable error tracking
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    // only send events when using https
    useSecureSessionCookie: true,
    // send traces to DD so we can associate requests with the backend
    allowedTracingUrls: [
      {
        // allow current domain traces
        match: location.origin,
        propagatorTypes: ['tracecontext'],
      },
    ],
    traceSampleRate: 20,
    traceContextInjection: 'all',
    propagateTraceBaggage: true,
    // use the automed-nginx proxy to send data from /dd to datadog's intake behind the scenes
    // Only set proxy in prod environments
    ...(!isDev && {
      proxy: (options) =>
        `https://${location.hostname}/dd${options.path}?${options.parameters}`,
    }),
    plugins: [reactPlugin({ router: true })],
    enableExperimentalFeatures: ['feature-flag-evaluation'],
  })

  const userFullName = getUserFullName()

  // Universal event attributes are tagged on every Datadog RUM action. Only the values available synchronously at
  // boot are set here; `identifyDatadogUser` fills in the rest once the userConfig response resolves.
  datadogRum.setGlobalContext({
    customer: CUSTOMER,
    environment: DD_RUM_ENV,
    user_name: userFullName,
  })

  datadogRum.setUserProperty('name', userFullName)
}

/**
 * Tag the RUM session with the surface currently in view. Both keys carry the same value: `product_id` is what
 * dashboards facet on, and `clinical_app_version` predates it. Called again on every navigation, because the legacy
 * editor and every Moose surface are routes in one router tree, so a session moves between them without a reload and
 * a value set once at boot goes stale.
 */
export const setDatadogAppVersion = (version: string) => {
  datadogRum.setGlobalContextProperty('clinical_app_version', version)
  datadogRum.setGlobalContextProperty('product_id', version)
}

/** The identity attributes that are only known once the userConfig request resolves. */
export interface DatadogUserIdentity {
  userId: string
  launchId: string | undefined
  appLaunchLogId: number | undefined
  userGroups: string[]
  userGroupIds: string[]
  practitionerId: string | undefined
  practitionerIds: string[]
  hashedPatientId: string | undefined
}

/**
 * Tag the RUM session with the identity attributes that are only known once the userConfig request resolves.
 *
 * Uses the property-level setters so the attributes already set at boot are merged rather than replaced. Safe to call
 * when RUM never booted: the SDK buffers context set before `init` and only drains the buffer on `init`, so these
 * values go nowhere for read-only sessions and environments without RUM credentials.
 */
export const identifyDatadogUser = ({
  userId,
  launchId,
  appLaunchLogId,
  userGroups,
  userGroupIds,
  practitionerId,
  practitionerIds,
  hashedPatientId,
}: DatadogUserIdentity) => {
  datadogRum.setGlobalContextProperty('user_id', userId)
  datadogRum.setGlobalContextProperty('launch_id', launchId)
  datadogRum.setGlobalContextProperty('app_launch_log_id', appLaunchLogId)
  // Group names collide across customers and change on rename, so the IDs are tagged alongside them. Both lists
  // share an ordering, so the nth ID names the nth group.
  datadogRum.setGlobalContextProperty('user_group', userGroups)
  datadogRum.setGlobalContextProperty('user_group_id', userGroupIds)
  // Both are tagged because neither contains the other: Epic issues several IDs per practitioner, and the handshake ID
  // is absent from the set once it has been deleted by a duplicate-user merge or a FHIR rejection.
  datadogRum.setGlobalContextProperty('practitioner_id', practitionerId)
  datadogRum.setGlobalContextProperty('practitioner_ids', practitionerIds)
  // Tagged in its hashed form so RUM can be joined to the Amplitude events, which only carry the hash.
  // This is the launch patient and is not updated on navigation, unlike the raw `patient_id` that the document fetch
  // tags; the two therefore disagree once the user opens a different patient in the same session.
  datadogRum.setGlobalContextProperty('hashed_patient_id', hashedPatientId)

  datadogRum.setUserProperty('id', userId)
}

/**
 * Report an error React surfaced -- uncaught, caught by a boundary, or recoverable -- to Datadog RUM.
 *
 * This is the only place React errors are reported. Boundaries deliberately do not report the errors they catch,
 * because React calls the root `onCaughtError` handler for those too and the error would be filed twice.
 *
 * The console output is for dev only, because RUM turns every `console.error` into an error event of its own and the
 * error is already being reported here.
 */
export const logReactErrorToDatadog = (
  error: unknown,
  errorInfo: ErrorInfo,
  prefix: string = '',
) => {
  addReactError(error as Error, errorInfo)

  if (isDev) {
    // eslint-disable-next-line no-console
    console.error(`${prefix}Error:`, error, errorInfo)
  }
}
